import { IonPage, IonButton, IonContent, IonSpinner } from '@ionic/react';
import { useEffect, useState } from 'react';
import { useIsMobile } from '../hooks/useMediaQuery';
import { courseService } from '@/services/courseService';
import { Certification } from '@/types/certification';

export function CertificationCoursesPage() {
  const isMobile = useIsMobile();
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const certificationsData = await courseService.getCertificationsWithCourses();
        setCertifications(certificationsData);
      } catch (error) {
        // Error modal presented by API
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <IonPage>
        <IonContent className="page-content bg-gray-50">
          <div className="flex h-full items-center justify-center">
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent className="page-content bg-gray-50">
        <div className={`page-header ${isMobile ? 'page-header-mobile' : 'page-header-desktop'}`}>
          <div className="title">Certifications</div>
        </div>

        <div className="py-6">
          {/* Certification Info Cards */}
          {certifications.map((certification) => {
            if (certification.name === 'REV Certification') {
              return (
                <div key={certification.id} className="mb-6">
                  <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-200">
                    {/* Header with Icon */}
                    <div className="flex flex-col items-center text-center mb-6">
                      <div className="bg-pink-50 rounded-2xl p-4 mb-4">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#E95563" strokeWidth="2">
                          <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                          <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                        </svg>
                      </div>
                      <h2 className="text-3xl font-bold mb-4">Get Rev Leasing Certified</h2>
                      <p className="text-lg max-w-3xl">
                        You will earn a unique certification in sales and customer service for multifamily
                        leasing—skills training you won't find anywhere else. Rev Leasing Certification equips you
                        with the expertise to convert more leads and close more leases.
                      </p>
                    </div>

                    {/* Two Column Benefits */}
                    <div className="grid md:grid-cols-2 gap-8 mt-8">
                      {/* Confidence */}
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-[#E95563] mb-4">Confidence</h3>
                        <p className="text-gray-800">
                          Equip every leasing professional to close more leases, faster. You gain the
                          confidence that comes from mastering proven techniques.
                        </p>
                        <div className="mt-6">
                          <IonButton
                            color="primary"
                          >
                            View All Courses
                          </IonButton>
                        </div>
                      </div>

                      {/* Continuous Development */}
                      <div className="text-center">
                        <h3 className="text-2xl font-bold text-[#E95563] mb-4">Continuous Development</h3>
                        <p className="text-gray-800">
                          Adaptive learning paths that evolve with each team member. Gamified content keeps
                          engagement high and ensures skills don't go stale after initial certification.
                        </p>
                        <div className="mt-6">
                          <IonButton
                            color="primary"
                          >
                            Start Learning
                          </IonButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          })}

          {/* FHA Certification Card */}
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" style={{ minHeight: '250px' }}>
              {/* Coming Soon Header */}
              <div className="text-center pt-6 pb-2">
                <p className="text-lg font-semibold text-gray-600">Coming Soon...</p>
              </div>

              <div className="grid lg:grid-cols-2 gap-6 p-8 pt-4">
                {/* Column 1: Icon, Title and Description */}
                <div className="flex flex-col items-center text-center lg:border-r lg:border-gray-200">
                  <div className="mb-4">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#D85F67" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <path d="M12 18v-6"></path>
                      <path d="M9 15l3 3 3-3"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold mb-4">Fair Housing Act Certification</h3>
                  <p className="text-gray-700">
                    This course provides an understanding of the Federal Fair Housing laws and offered a detailed
                    explanation of their application to your work. Learn through true-to-life examples to apply
                    your knowledge and skills.
                  </p>
                </div>

                {/* Column 2: Benefits List */}
                <div>
                  <p className="font-semibold mb-3">This course will help you:</p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="text-[#D85F67] mr-2">•</span>
                      <span>Learn the Federal Fair Housing laws, guidelines, and their application to our industry</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#D85F67] mr-2">•</span>
                      <span>Discover how to define and identify discrimination and harassment</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#D85F67] mr-2">•</span>
                      <span>Understand reasonable accommodations and modifications</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#D85F67] mr-2">•</span>
                      <span>Master documentation and compliance strategies</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#D85F67] mr-2">•</span>
                      <span>Explore and understand the complexities of applicant screening</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center pb-4 pt-2">
                <p className="text-xs text-gray-500">This course is approved by DPOR and TDHCA.</p>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}

